import { Button, Form, Modal, notification, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getGroupById, updateGroupStudents } from "store/slices/groupsSlice";
import { closeModal } from "store/slices/modalsSlice";
import { getStudentFormationsForFormation } from "store/slices/trainingsSlice";

const UpdateStudentTrainingGroupModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form?.useForm();

  // Sélecteurs Redux
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { loading, group } = useSelector((state) => state?.groups);
  const { availableStudentFormations } = useSelector(
    (state) => state?.trainings
  );

  // États locaux
  const [selectedGroupState, setSelectedGroupState] = useState(
    group?.groupState
  );

  // État du modal
  const open = isModalOpen && modalType === "update_training_group_students";
  const groupId = modalProps?.groupId;

  // Charger les étudiants disponibles et initialiser le formulaire
  useEffect(() => {
    if (open && group?.formationId?._id) {
      dispatch(
        getStudentFormationsForFormation({
          trainingId: group?.formationId?._id,
          groupId,
        })
      );

      form?.setFieldsValue({
        groupState: group?.groupState,
        students: group?.students?.map((s) => s?.studentFormationId?._id),
      });

      setSelectedGroupState(group?.groupState);
    }
  }, [dispatch, open, group, form, groupId]);

  // Gérer la soumission du formulaire
  const onSubmit = async (values) => {
    try {
      const formattedValues = {
        groupState: values?.groupState,
        students: values?.students?.map((studentFormationId) => ({
          studentFormationId,
        })),
      };

      await dispatch(
        updateGroupStudents({
          groupId,
          data: formattedValues,
        })
      )?.unwrap();

      await dispatch(getGroupById(groupId));

      notification?.success({
        message: t("groups.crud.form.update.success"),
        duration: 3,
      });

      onClose();
    } catch (error) {
      notification?.error({
        message: t("groups.crud.form.update.error"),
        description: error?.message || t("common.error.generic"),
        duration: 4,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  // Options pour le type de groupe
  const groupStateOptions =
    group?.groupeType === "online"
      ? ["particulier", "miniGroupe"]
      : ["particulier", "miniGroupe", "groupe"];

  // Validation du nombre d'étudiants
  const validateStudentsCount = (_, value) => {
    if (!value || !selectedGroupState) return Promise?.resolve();

    const count = value?.length;

    switch (selectedGroupState) {
      case "particulier":
        if (count !== 1) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.single")
          );
        }
        break;
      case "miniGroupe":
        if (count < 2 || count > 3) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.mini")
          );
        }
        break;
      case "groupe":
        if (count < 4) {
          return Promise?.reject(
            t("groups.crud.form.error.validation.students.group")
          );
        }
        break;
      default:
        return Promise?.resolve();
    }

    return Promise?.resolve();
  };

  return (
    <Modal
      title={t("groups.crud.form.update.student.title")}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => form?.submit()}
          loading={loading}
        >
          {t("common.save")}
        </Button>,
      ]}
      destroyOnClose
      centered
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="groupState"
          label={t("groups.crud.form.label.trainingState")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.trainingState"),
            },
          ]}
        >
          <Select
            placeholder={t("groups.crud.form.placeholder.state")}
            onChange={(value) => {
              setSelectedGroupState(value);
              // Ne pas réinitialiser les étudiants sélectionnés
              form?.validateFields(["students"])?.catch(() => {});
            }}
          >
            {groupStateOptions?.map((state) => (
              <Select.Option key={state} value={state}>
                {t(`groups.state.${state?.replace(/\s+/g, "")?.toLowerCase()}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="students"
          label={t("groups.crud.form.label.students")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.error.required.students"),
            },
            { validator: validateStudentsCount },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t("groups.crud.form.placeholder.students")}
            loading={loading}
            onChange={() => {
              // Revalider le champ immédiatement après un changement
              form?.validateFields(["students"])?.catch(() => {});
            }}
          >
            {availableStudentFormations?.map((student) => (
              <Select.Option
                key={student?.studentFormationId}
                value={student?.studentFormationId}
              >
                {`${student?.studentName} - ${student?.payment?.amount || 0} / ${
                  student?.price?.[student?.payment?.currency] || 0
                } (${student?.payment?.currency || "DZD"})`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateStudentTrainingGroupModal;
